'use client';

import { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { cn } from '@/lib/utils';
import { resolveI18nText } from '@/sanity/globals/I18n';
import { IPricingCategory } from '@/sanity/lib/definitions';
import {
  Tooltip,
  TooltipTrigger,
  TooltipCustomContent,
  TooltipProvider,
} from '@/shadcn/ui/tooltip';
import { resolveNewBadgeLabel } from './helpers';

const ease = [0.32, 0.72, 0, 1] as const;

export default function CategorySelector({
  categories,
  activeKey,
  onSelect,
  language,
}: {
  categories: IPricingCategory[];
  activeKey: string;
  onSelect: (key: string) => void;
  language?: string;
}) {
  return (
    <div className="grid w-full grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-[1.25rem]">
      {categories.map((category) => (
        <CategoryCard
          key={category.key}
          category={category}
          isActive={category.key === activeKey}
          onSelect={() => onSelect(category.key ?? '')}
          language={language}
        />
      ))}
    </div>
  );
}

function CategoryCard({
  category,
  isActive,
  onSelect,
  language,
}: {
  category: IPricingCategory;
  isActive: boolean;
  onSelect: () => void;
  language?: string;
}) {
  const name = resolveI18nText(category.name, category.nameI18n, language);
  const chip = resolveI18nText(category.chip, category.chipI18n, language);
  const description = resolveI18nText(category.description, category.descriptionI18n, language);
  const tooltip = resolveI18nText(category.tooltip, category.tooltipI18n, language);

  return (
    // The info trigger is a sibling of the card button, not a child — nesting
    // interactive elements inside a <button> is invalid and swallows clicks.
    <div className="relative">
      <motion.button
        type="button"
        onClick={onSelect}
        aria-pressed={isActive}
        className="relative w-full h-full overflow-hidden rounded-[0.5rem] border px-[2rem] py-[2rem] md:px-[2.75rem] md:py-[2.5rem] text-left"
        animate={{
          backgroundColor: isActive ? '#0d2922' : '#f5f5f5',
          borderColor: isActive ? 'rgba(0,0,0,0.14)' : 'rgba(0,0,0,0.1)',
        }}
        transition={{ duration: 0.35, ease }}
        whileHover={!isActive ? { backgroundColor: '#efefef' } : undefined}
      >
        <div className="relative flex items-center gap-[1.25rem] md:gap-[1.5rem]">
          <StatusIcon isActive={isActive} />

          <div className={cn('flex flex-col gap-[1rem] md:gap-[1.25rem] min-w-0', tooltip && 'pr-[3.5rem]')}>
            <div className="flex items-center gap-[0.75rem] md:gap-[1rem] flex-wrap">
              <motion.span
                className="mb-0 text-[2.5rem] md:text-[3rem] leading-[1.3] tracking-[-0.125rem] md:tracking-[-0.15rem] font-geist font-medium"
                animate={{ color: isActive ? '#ffffff' : '#000000' }}
                transition={{ duration: 0.32, ease }}
              >
                {name}
              </motion.span>

              {chip && <CategoryChip label={chip} isActive={isActive} />}
              {category.isNew && <NewChip language={language} />}
            </div>

            {description && (
              <motion.p
                className="mb-0 whitespace-pre-line text-[1.75rem] md:text-[2rem] leading-[1.3] font-geist font-normal"
                animate={{ color: isActive ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)' }}
                transition={{ duration: 0.32, ease }}
              >
                {description}
              </motion.p>
            )}
          </div>
        </div>
      </motion.button>

      {tooltip && (
        <CategoryInfoButton tooltip={tooltip} label={name ?? ''} isActive={isActive} />
      )}
    </div>
  );
}

function CategoryInfoButton({
  tooltip,
  label,
  isActive,
}: {
  tooltip: string;
  label: string;
  isActive: boolean;
}) {
  const [open, setOpen] = useState(false);

  return (
    <div className="absolute top-[1.75rem] right-[1.75rem] md:top-[2.25rem] md:right-[2.25rem] z-10">
      <TooltipProvider delayDuration={100}>
        <Tooltip open={open} onOpenChange={setOpen}>
          <TooltipTrigger asChild>
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setOpen((o) => !o);
              }}
              className={cn(
                'flex items-center transition-colors cursor-pointer',
                isActive ? 'text-white/50 hover:text-white' : 'text-black/40 hover:text-black/70',
              )}
              aria-label={`${label} info`}
            >
              <InfoIcon />
            </button>
          </TooltipTrigger>
          <TooltipCustomContent
            side="top"
            className="max-w-[36rem] rounded-[0.375rem] bg-[#103037] px-[1.5rem] py-[1rem] text-[1.75rem] font-geist leading-[1.4] text-white"
          >
            {tooltip}
          </TooltipCustomContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  );
}

function InfoIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 16 16"
      fill="none"
      className="shrink-0 w-[2.25rem] h-[2.25rem]"
      aria-hidden="true"
    >
      <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1" fill="none" />
      <circle cx="8" cy="5" r="0.9" fill="currentColor" />
      <rect x="7.15" y="7" width="1.7" height="5" rx="0.85" fill="currentColor" />
    </svg>
  );
}

function CategoryChip({ label, isActive }: { label: string; isActive: boolean }) {
  return (
    <motion.span
      className="inline-flex items-center justify-center px-[1.125rem] py-[0.5rem] rounded-[7.5rem] text-[1.75rem] leading-[1.3] tracking-[-0.0875rem] font-geist font-normal whitespace-nowrap border"
      animate={{
        backgroundColor: isActive ? '#1aa87f' : '#ffffff',
        borderColor: isActive ? '#1aa87f' : 'rgba(16,48,55,0.12)',
        color: isActive ? '#ffffff' : '#103037',
      }}
      transition={{ duration: 0.32, ease }}
    >
      {label}
    </motion.span>
  );
}

function NewChip({ language }: { language?: string }) {
  return (
    <span className="inline-flex items-center justify-center px-[1.125rem] py-[0.5rem] rounded-[7.5rem] bg-[#1aa87f] text-white text-[1.75rem] leading-[1.3] tracking-[-0.0875rem] font-geist font-normal whitespace-nowrap">
      {resolveNewBadgeLabel(language)}
    </span>
  );
}

function StatusIcon({ isActive }: { isActive: boolean }) {
  return (
    <div className="relative w-[3rem] h-[3rem] md:w-[3.75rem] md:h-[3.75rem] shrink-0">
      <AnimatePresence mode="wait" initial={false}>
        {isActive ? (
          <motion.svg
            key="checked"
            viewBox="0 0 30 30"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="absolute inset-0 w-full h-full"
            initial={{ scale: 0.7, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.7, opacity: 0 }}
            transition={{ duration: 0.25, ease }}
            aria-hidden="true"
          >
            <circle cx="15" cy="15" r="14.5" fill="#ffffff" stroke="rgba(0,0,0,0.14)" />
            <path
              d="M9 15L13 19L21 11"
              stroke="#0d2922"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </motion.svg>
        ) : (
          <motion.svg
            key="empty"
            viewBox="0 0 30 30"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="absolute inset-0 w-full h-full"
            initial={{ scale: 0.7, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.7, opacity: 0 }}
            transition={{ duration: 0.25, ease }}
            aria-hidden="true"
          >
            <circle cx="15" cy="15" r="14.5" fill="#ffffff" stroke="rgba(0,0,0,0.15)" />
          </motion.svg>
        )}
      </AnimatePresence>
    </div>
  );
}
